import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { takeUntil, filter, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { BaseGenderOptions } from '../../../../../shared/constants/base-gender-options';
import { BaseButtonSize } from '../../../../../shared/components/button/base-button.component';
import {
  BranchListItemResponse,
  DepartmentListItemResponse,
  GroupListItemResponse,
  LocationListItemResponse,
} from '../../../../../../api';
import { InviteUserPspForm, InviteUserCustomerForm } from '../../../_interfaces/base-user-management.interface';
import { BASE_ROLES } from '../../../../../core/constants/base-roles';
import { BaseRolesService } from '../../../../../core/services/base-roles.service';
import { BaseCoreState, baseAdministrationActions, baseAdministrationSelectors } from '../../../../../_store';

@Component({
  selector: 'base-invite-user-form',
  templateUrl: './base-invite-user-form.component.html',
})
export class BaseInviteUserFormComponent implements OnInit, OnDestroy {
  @Input() form: FormGroup<InviteUserPspForm | InviteUserCustomerForm>;
  @Input() modalRef: NgbModalRef;
  @Input() groups: GroupListItemResponse[];
  @Input() pspBranches: BranchListItemResponse[];
  @Input() customerDepartments: DepartmentListItemResponse[];
  @Input() isFormSubmitted = false;
  @Input() isPSP = false;
  @Output() inviteUser = new EventEmitter<boolean>();

  genderOptions = BaseGenderOptions;
  buttonSizes = BaseButtonSize;
  baseRoles = BASE_ROLES;
  isAdmin = false;
  isWorkCouncil = false;
  locations: LocationListItemResponse[] = [];

  private destroy$ = new Subject();

  constructor(private rolesService: BaseRolesService, private store: Store<BaseCoreState>) {}

  get hasGroupControl(): boolean {
    return this.form.contains('permissionGroup');
  }

  get hasCustomerLocationControl(): boolean {
    return this.form.contains('customerLocations');
  }

  get hasCustomerDepartmentsControl(): boolean {
    return this.form.contains('customerDepartments');
  }

  ngOnInit(): void {
    this.isAdmin = this.rolesService.hasAdminRole();
    this.isWorkCouncil = this.rolesService.hasWorkCouncilRole();

    if (!this.isPSP) {
      this.getCustomerLocations();
      this.customerFormRoleControlHandler(this.form as FormGroup<InviteUserCustomerForm>);
    }
  }

  ngOnDestroy() {
    if (!this.isPSP) {
      this.removeOptionalFormControls(this.form as FormGroup<InviteUserCustomerForm>);
    }
  }

  confirmSendingInvitation(): void {
    this.isFormSubmitted = true;
    this.inviteUser.emit();
  }

  cancelSendingInvitation(): void {
    this.modalRef.close();
  }

  customerFormRoleControlHandler(customerForm: FormGroup<InviteUserCustomerForm>) {
    customerForm.controls.role.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((role) => {
      switch (role) {
        case BASE_ROLES.CUSTOMER_WORK_COUNCIL:
          this.addControls(customerForm, ['customerLocations']);
          this.removeControls(customerForm, ['permissionGroup', 'customerDepartments']);
          break;
        case BASE_ROLES.CUSTOMER_USER:
          this.addControls(customerForm, ['permissionGroup', 'customerDepartments']);
          this.removeControls(customerForm, ['customerLocations']);
          break;
        case BASE_ROLES.CUSTOMER_ADMIN:
          this.removeOptionalFormControls(customerForm);
      }
    });
  }

  private addControls<T extends { [K in keyof T]: AbstractControl<any, any> }>(
    form: FormGroup<T>,
    controlNames: Extract<keyof T, string>[]
  ) {
    controlNames.forEach((controlName) => {
      if (!form.contains(controlName)) {
        (form as FormGroup).addControl(
          controlName,
          new FormControl(null as any, { nonNullable: true, validators: [Validators.required] })
        );
      }
    });
  }

  private removeControls<T extends { [K in keyof T]: AbstractControl<any, any> }>(
    form: FormGroup<T>,
    controlNames: Extract<keyof T, string>[]
  ): void {
    controlNames.forEach((controlName) => {
      if (form.contains(controlName)) {
        (form as FormGroup).removeControl(controlName);
      }
    });
  }

  private getCustomerLocations() {
    this.store
      .select(baseAdministrationSelectors.getLocationsData)
      .pipe(
        takeUntil(this.destroy$),
        filter((value) => !!value.ok),
        map((value) => value.data as LocationListItemResponse[])
      )
      .subscribe((locations) => {
        this.locations = locations;
      });

    this.store.dispatch(baseAdministrationActions.loadLocationsData({ payload: { isActive: true } }));
  }

  private removeOptionalFormControls(customerForm: FormGroup<InviteUserCustomerForm>) {
    this.removeControls(customerForm, ['permissionGroup', 'customerDepartments', 'customerLocations']);
  }
}
